import * as THREE from "three";
import type { PlaneSkin } from "../shared/protocol";

const SKIN_BODIES: Record<PlaneSkin, number> = {
  0: 0xff5555, // crimson
  1: 0x4ec9ff, // azure
  2: 0xf7d04c, // gold
  3: 0x6cd07a, // jungle
};

const SKIN_TRIM: Record<PlaneSkin, number> = {
  0: 0x222222,
  1: 0x0a3a4a,
  2: 0x4a3a00,
  3: 0x244022,
};

const SKIN_GLASS: Record<PlaneSkin, number> = {
  0: 0x66c9ff,
  1: 0xb6ecff,
  2: 0xffe28a,
  3: 0xc4ffd0,
};

// Procedurally builds a low-poly fighter. Shared silhouette across skins —
// each skin tweaks color and a small element (canopy bubble, tail fin shape, wingtip).
export function buildPlane(skin: PlaneSkin = 0): THREE.Group {
  const g = new THREE.Group();
  g.name = "Plane";

  const bodyColor = SKIN_BODIES[skin];
  const trimColor = SKIN_TRIM[skin];
  const glassColor = SKIN_GLASS[skin];

  const bodyMat = new THREE.MeshLambertMaterial({ color: bodyColor, flatShading: true });
  const trimMat = new THREE.MeshLambertMaterial({ color: trimColor, flatShading: true });
  const glassMat = new THREE.MeshLambertMaterial({ color: glassColor, flatShading: true, emissive: 0x111122 });

  // Fuselage — stretched octahedron-like shape
  const fuselage = new THREE.Mesh(
    new THREE.CylinderGeometry(0.8, 0.4, 6, 6).rotateZ(Math.PI / 2),
    bodyMat,
  );
  g.add(fuselage);

  // Nose cone
  const nose = new THREE.Mesh(new THREE.ConeGeometry(0.4, 1.5, 6).rotateZ(-Math.PI / 2), trimMat);
  nose.position.x = 3.5;
  g.add(nose);

  // Wings (single low-poly trapezoid)
  const wingShape = new THREE.Shape();
  wingShape.moveTo(-1.2, 0);
  wingShape.lineTo(1.4, 0);
  wingShape.lineTo(0.8, 4.2);
  wingShape.lineTo(-0.6, 4.2);
  wingShape.lineTo(-1.2, 0);
  const wingGeo = new THREE.ExtrudeGeometry(wingShape, { depth: 0.18, bevelEnabled: false });
  wingGeo.rotateX(-Math.PI / 2);
  wingGeo.translate(0, -0.05, 0);
  const wingR = new THREE.Mesh(wingGeo, bodyMat);
  g.add(wingR);
  const wingL = new THREE.Mesh(wingGeo, bodyMat);
  wingL.scale.z = -1;
  g.add(wingL);

  // Wingtip element (varies per skin)
  const tipGeo = skin === 0 ? new THREE.BoxGeometry(0.4, 0.3, 0.4)
                : skin === 1 ? new THREE.ConeGeometry(0.3, 0.9, 4)
                : skin === 2 ? new THREE.SphereGeometry(0.35, 6, 4)
                : new THREE.CylinderGeometry(0.25, 0.25, 0.8, 6);
  const tipR = new THREE.Mesh(tipGeo, trimMat);
  tipR.position.set(0.1, 0, 4.0);
  g.add(tipR);
  const tipL = tipR.clone();
  tipL.position.z = -4.0;
  g.add(tipL);

  // Canopy / cockpit bubble
  const canopyGeo = skin === 1 ? new THREE.SphereGeometry(0.65, 8, 6, 0, Math.PI * 2, 0, Math.PI / 2)
                  : new THREE.SphereGeometry(0.55, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2);
  const canopy = new THREE.Mesh(canopyGeo, glassMat);
  canopy.position.set(0.8, 0.55, 0);
  g.add(canopy);

  // Tail fin (vertical) — varies per skin
  const tailH = skin === 2 ? 1.6 : 1.2;
  const tailShape = new THREE.Shape();
  tailShape.moveTo(0, 0);
  tailShape.lineTo(1.5, 0);
  tailShape.lineTo(1.0, tailH);
  tailShape.lineTo(0.0, tailH * 0.7);
  tailShape.lineTo(0, 0);
  const tailGeo = new THREE.ExtrudeGeometry(tailShape, { depth: 0.16, bevelEnabled: false });
  tailGeo.rotateY(Math.PI / 2);
  tailGeo.translate(-3.0, 0.5, -0.08);
  const tail = new THREE.Mesh(tailGeo, bodyMat);
  g.add(tail);

  // Horizontal stabilizers
  const hShape = new THREE.Shape();
  hShape.moveTo(0, 0); hShape.lineTo(1.2, 0); hShape.lineTo(0.8, 1.4); hShape.lineTo(0, 1.4); hShape.lineTo(0, 0);
  const hGeo = new THREE.ExtrudeGeometry(hShape, { depth: 0.14, bevelEnabled: false });
  hGeo.rotateX(-Math.PI / 2);
  hGeo.translate(-2.9, 0.1, 0);
  const hR = new THREE.Mesh(hGeo, bodyMat);
  g.add(hR);
  const hL = new THREE.Mesh(hGeo, bodyMat);
  hL.scale.z = -1;
  g.add(hL);

  // Engine flame holder (used as exhaust origin)
  const exhaust = new THREE.Object3D();
  exhaust.name = "exhaust";
  exhaust.position.set(-3.4, 0, 0);
  g.add(exhaust);

  // Forward muzzle marker
  const muzzle = new THREE.Object3D();
  muzzle.name = "muzzle";
  muzzle.position.set(4.4, 0, 0);
  g.add(muzzle);

  return g;
}
